import React, { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, TextField, CircularProgress, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

// Buscador inteligente de direcciones sobre OpenStreetMap (Nominatim), acotado a Argentina.
// Detecta automáticamente si el texto es un código postal o una dirección:
//   - CP argentino  -> busca la localidad y autocompleta Localidad/Provincia/CP.
//   - Dirección     -> sugiere direcciones y autocompleta Calle/Nro/Localidad/Provincia/CP.
// Siempre limita a AR (countrycodes=ar) y descarta cualquier resultado de otro país.
//
// No persiste lat/lon/displayName: solo autocompleta los campos que el egreso ya guarda.
// lat/lon/displayName viajan en el objeto seleccionado por si el consumidor los necesita
// en runtime, pero acá no se envían al backend.

const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";
// jsonv2 + addressdetails para desglosar calle/localidad/provincia; es en español.
const COMMON_PARAMS = "format=jsonv2&addressdetails=1&countrycodes=ar&limit=6&accept-language=es";

// CP argentino: 4 dígitos (CPC viejo) / letra+4 dígitos / letra+4 dígitos+3 letras (CPA).
// Ej: 5000, X5000, C1425DFG.
const CP_REGEX = /^[A-Za-z]?\d{4}([A-Za-z]{3})?$/;
export const isPostalCode = (raw) => CP_REGEX.test(String(raw ?? "").trim());

// Núcleo de 4 dígitos del CP: Nominatim resuelve por el CPC numérico, no por el CPA
// alfanumérico completo. De "C1425DFG" o "X5000" extraemos "1425" / "5000".
const postalCore = (raw) => {
  const m = String(raw ?? "").trim().toUpperCase().match(/\d{4}/);
  return m ? m[0] : "";
};

// Los 4 dígitos del postcode de un resultado (CPA "S2000" -> "2000"). "" si no hay.
const resultPostalCore = (r) => {
  const m = String(r?.address?.postcode || "").match(/\d{4}/);
  return m ? m[0] : "";
};

// Normaliza un resultado de Nominatim a los campos que usa el formulario de egreso.
const mapResult = (r, mode) => {
  const a = r.address || {};
  return {
    mode, // "postal" | "address"
    street: a.road || a.pedestrian || a.footway || a.residential || "",
    streetNumber: a.house_number || "",
    city:
      a.city || a.town || a.village || a.municipality || a.city_district || a.county || "",
    province: a.state || "",
    postcode: a.postcode || "",
    country: "Argentina",
    lat: r.lat,
    lon: r.lon,
    displayName: r.display_name,
    placeId: r.place_id,
  };
};

// Pega a Nominatim con los parámetros comunes y devuelve SOLO resultados de Argentina.
const nominatim = async (extraParams, signal) => {
  const resp = await fetch(`${NOMINATIM_URL}?${COMMON_PARAMS}&${extraParams}`, {
    signal,
    headers: { Accept: "application/json" },
  });
  if (!resp.ok) throw new Error(`Nominatim ${resp.status}`);
  const data = await resp.json();
  return (Array.isArray(data) ? data : []).filter(
    (r) => (r.address?.country_code || "ar").toLowerCase() === "ar"
  );
};

// Deduplica localidades (mismo city+province) para no repetir opciones en modo CP.
const dedupByLocality = (list) => {
  const seen = new Set();
  return list.filter((o) => {
    const key = `${o.city}|${o.province}`.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export default function AddressSearch({ onSelect, label, placeholder, size = "small" }) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const trimmed = inputValue.trim();
  const postal = useMemo(() => isPostalCode(trimmed), [trimmed]);

  useEffect(() => {
    // Limpio debounce/fetch anterior en cada tecla.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    // CP: dispara al matchear el formato. Dirección: recién con 3+ caracteres.
    const ready = postal ? postalCore(trimmed).length === 4 : trimmed.length >= 3;
    if (!ready) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        let results;
        if (postal) {
          const code4 = postalCore(trimmed);
          // 1) Búsqueda estructurada por CP (limpia cuando Nominatim tiene el dato).
          let raw = await nominatim(`postalcode=${encodeURIComponent(code4)}&country=Argentina`, controller.signal);
          let filtered = raw.filter((r) => resultPostalCore(r) === code4);
          // 2) Fallback: hay CPs (ej. 2000/Rosario) que el structured no resuelve. Vamos
          //    por texto libre y nos quedamos SOLO con los que matchean el CP (descarta ruido).
          if (!filtered.length) {
            raw = await nominatim(`q=${encodeURIComponent(code4)}`, controller.signal);
            filtered = raw.filter((r) => resultPostalCore(r) === code4);
          }
          results = dedupByLocality(filtered.map((r) => mapResult(r, "postal")));
        } else {
          const raw = await nominatim(`q=${encodeURIComponent(trimmed)}`, controller.signal);
          results = raw.map((r) => mapResult(r, "address"));
        }
        setOptions(results);
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Error buscando en Nominatim:", e);
          setOptions([]);
        }
      } finally {
        setLoading(false);
      }
    }, 600); // Respeta el límite ~1 req/seg del Nominatim público.

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [trimmed, postal]);

  // Etiqueta legible de cada opción según el modo.
  const optionLabel = (opt) => {
    if (!opt || typeof opt === "string") return opt || "";
    if (opt.mode === "postal") {
      const loc = [opt.city, opt.province].filter(Boolean).join(", ");
      return `${opt.postcode || postalCore(trimmed)} — ${loc || "Localidad"}`;
    }
    return opt.displayName || "";
  };

  return (
    <Autocomplete
      freeSolo
      size={size}
      options={options}
      loading={loading}
      // Búsqueda server-side: no filtrar client-side.
      filterOptions={(x) => x}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(o, val) => o.placeId === val.placeId}
      inputValue={inputValue}
      onInputChange={(_e, val, reason) => {
        // "input": tipeo del usuario. "clear": botón X -> vaciar input y opciones.
        // "reset" (post-selección) se ignora para no volcar el display_name al input.
        if (reason === "input") setInputValue(val);
        else if (reason === "clear") {
          setInputValue("");
          setOptions([]);
        }
      }}
      onChange={(_e, value) => {
        if (value && typeof value === "object" && typeof onSelect === "function") {
          onSelect(value);
        }
      }}
      noOptionsText={
        postal
          ? "Sin localidad para ese código postal"
          : trimmed.length < 3
            ? "Escribí una dirección o un código postal"
            : "Sin resultados en Argentina"
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || "Buscar dirección o código postal (Argentina)"}
          placeholder={placeholder || "Av. Corrientes 1234  ·  5000  ·  C1425"}
          size={size}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
